import React from 'react'
import useMediaQuery from '@/hooks/useMediaQuery'
import SortIcon from '@/assets/svgs/sort-icon.svg'
import SortIconActive from '@/assets/svgs/sort-icon-active.svg'
import SortIconActiveUp from '@/assets/svgs/sort-icon-active-up.svg'
import IconNoMessage from '@/assets/svgs/no-message.svg'
import DownloadIcon from '@/assets/svgs/download-large.svg'
import Button from '@/components/Button/Button'
import { ButtonType, ButtonVariant } from '@/components/Button/Button.type'
import Pagination from '@/components/Pagination/index'
import Loader from '@/components/Loader'
import PropTypes from 'prop-types'
import clsx from 'clsx'

const getColumnStyle = (headerKey, isMobile) => {
  if (headerKey === 'project_contract_name')
    return isMobile
      ? { flex: '0 0 200px', minWidth: '200px', maxWidth: '200px' }
      : { flex: '0 0 320px', minWidth: '320px', maxWidth: '320px' }
  if (headerKey === 'action') return { flex: '0 0 80px', minWidth: '80px', maxWidth: '80px' }
  if (headerKey === 'status' && isMobile)
    return { flex: '0 0 200px', minWidth: '200px', maxWidth: '200px' }
  return { flex: 1, minWidth: '120px' }
}

const MasterDataTable = ({
  headers,
  data,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageLimit = 10,
  onPageChange,
  onSort,
  sortColumn,
  sortOrder,
  getClassName,
  formatValue = value => value,
  emptyMessage = 'No Data Found!!!',
  emptyIcon,
  onRowClick,
  onDownloadCsv,
  loading = false,
  loadingContent = <Loader />,
}) => {
  const phoneBreakpoint = getComputedStyle(document.documentElement)
    .getPropertyValue('--breakpoint-phone')
    .trim()
  const isMobile = useMediaQuery(`(max-width: calc(${phoneBreakpoint} - 1px))`)
  const processedData = data || []

  const handleSort = headerKey => {
    if (onSort) {
      onSort(headerKey)
    }
  }

  const getPageLabel = (total, limit, current) => {
    const start = current * limit + 1
    const end = Math.min((current + 1) * limit, total)
    return `${start}-${end}`
  }

  const list = processedData?.map((item, index) => {
    return (
      <div
        className="master-data-table__table__body__row flex w-full h-[76px] items-center"
        key={index}
      >
        <div
          className="flex w-full h-full cursor-pointer"
          onClick={() => onRowClick && onRowClick(item)}
        >
          {Object.keys(headers).map(headerKey => (
            <div
              key={headerKey}
              className={clsx(
                'box-border flex items-center border-b border-grey-90 type-body-2 break-words py-0 pr-2 pl-6',
                headerKey === 'action' && 'justify-center p-0 pointer-events-none'
              )}
              style={getColumnStyle(headerKey, isMobile)}
              data-testid={headerKey}
              onClick={e => headerKey === 'action' && e.stopPropagation()}
            >
              {headerKey === 'action' ? (
                <div
                  className="flex justify-center items-center w-full pointer-events-auto"
                  onClick={e => e.stopPropagation()}
                >
                  <Button
                    type={ButtonType.GHOST}
                    variant={ButtonVariant.STANDARD}
                    prefixIcon={<img src={DownloadIcon} alt="Download" />}
                    onClick={() => onDownloadCsv && onDownloadCsv(item)}
                    disabled={loading}
                  />
                </div>
              ) : (
                <div>{formatValue(item?.[headerKey], headerKey)}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  })

  return (
    <div className="relative border border-grey-90 rounded-xl bg-white overflow-x-auto">
      <div>
        <div className="type-body-2 overflow-x-auto">
          <div>
            <div className="flex w-full h-[56.5px] items-center">
              <div className="flex w-full h-full">
                {Object.keys(headers).map(headerKey => (
                  <div
                    key={headerKey}
                    data-id={headerKey}
                    className={clsx(
                      'box-border flex items-center justify-between relative py-0 pr-4 pl-6 border-b border-grey-90',
                      "after:content-[''] after:absolute after:right-0 after:top-1/2 after:-translate-y-1/2 after:h-1/2 after:w-px after:bg-grey-90 last:after:hidden",
                      headerKey === 'action' ? 'justify-center' : 'cursor-pointer',
                      sortColumn === headerKey ? 'text-theme-50' : 'text-grey-50',
                      getClassName?.(headerKey)
                    )}
                    style={getColumnStyle(headerKey, isMobile)}
                    onClick={headerKey === 'action' ? null : () => handleSort?.(headerKey)}
                    onKeyDown={headerKey === 'action' ? null : () => handleSort?.(headerKey)}
                    id={headerKey}
                    role="none"
                  >
                    <div>{headers[headerKey]}</div>
                    {headerKey !== 'action' && (
                      <div className="leading-none ml-2">
                        <img
                          src={
                            sortColumn === headerKey
                              ? sortOrder === 'desc'
                                ? SortIconActive
                                : SortIconActiveUp
                              : SortIcon
                          }
                          alt="Sort"
                          className="sort-icon w-4 h-4 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {!loading && processedData?.length > 0 && list}
          </div>
        </div>
      </div>
      {loading && (
        <div className="pt-[42px] pb-[44px] bg-grey-95 rounded-b-xl flex items-center justify-center">
          {loadingContent}
        </div>
      )}
      {!loading && processedData?.length === 0 && (
        <div className="pt-[42px] px-8 pb-[44px] bg-grey-95 rounded-b-xl">
          <div className="flex items-center flex-col text-grey-50 text-center [&_svg]:mb-2">
            {emptyIcon ?? <img src={IconNoMessage} alt="No message" />}
            <div>{emptyMessage}</div>
          </div>
        </div>
      )}
      {!loading && processedData?.length > 0 && (
        <Pagination
          totalItems={totalItems}
          pageLimit={pageLimit}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          getPageLabel={getPageLabel}
        />
      )}
    </div>
  )
}

MasterDataTable.propTypes = {
  headers: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  pageLimit: PropTypes.number,
  onPageChange: PropTypes.func,
  onSort: PropTypes.func,
  sortColumn: PropTypes.string,
  sortOrder: PropTypes.string,
  getClassName: PropTypes.func,
  formatValue: PropTypes.func,
  emptyMessage: PropTypes.string,
  emptyIcon: PropTypes.node,
  onRowClick: PropTypes.func,
  onDownloadCsv: PropTypes.func,
  loading: PropTypes.bool,
  loadingContent: PropTypes.node,
}

export default MasterDataTable
